import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CssBaseline,
  Toolbar,
  Breadcrumbs,
  Typography,
  Link,
} from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "./Header_new";
import UserSidebar from "./UserSidebar_upated";
import { onMessage } from "firebase/messaging";
import { messaging } from "../utils/firebase";
import { enqueueSnackbar } from "notistack";
import { COFFEE_CALL_DUMMY_CHANNEL } from "../constants/config";
import { useAuth, useCurrentTalentProfile, useSidebarStore } from "../hooks";
import { useGetBookings } from "../services/api/booking.api";
import Footer from "../components/Footer/Footer";
import SocketHandler from "../utils/socketHandler";
import {
  getTalentSocketNotification,
  getUserSocketNotification,
} from "../utils/notificationHandler";
import useCurrentGTP from "../hooks/useGTP";
import { SocketNotificationData } from "../components/socketNotification/types";
import { playNotificationSound } from "../utils/notificationSound";

const UserLayout: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);

  const { userInfo } = useAuth();
  const { data, isLoading, error } = useGetBookings({
    key: ["bookings", userInfo?.id],
  });

  const { currentProfile } = useCurrentTalentProfile();

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);
  const location = useLocation();
  const navigate = useNavigate();
  const [badgeVisible, setBadgeVisible] = useState(false);

  const [socketNotification, setSocketNotification] = useState<any>([]);
  const [coffeeJoinChannel, setCoffeeJoinChannel] = useState<string | null>(
    null
  );

  const socketSubscribedRef = useRef(false);
 
  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current || !userInfo?.id) {
      return;
    }

    try {
      // Get socket instance and subscribe to messages
      const socketHandler = SocketHandler.getInstance();

      socketHandler.subscribeToNotification(
        userInfo.id.toString(),
        (message) => {
          if (message) {
            const notification = currentProfile?.id
              ? getTalentSocketNotification(message, currentProfile?.id)
              : getUserSocketNotification(message);
            if (notification) {
              setSocketNotification((prev: any) => [...prev, notification]);
              setBadgeVisible(true);
              playNotificationSound();
              console.log("notification are here in user layout", notification);
            }
          }

          // Handle the message here
        },
      );

      socketSubscribedRef.current = true;
      console.log("Successfully subscribed to notification socket");
    } catch (error) {
      console.error("Failed to subscribe to socket:", error);
    }

    // Cleanup subscription when component unmounts
    return () => {
      try {
        if (userInfo?.id) {
          const socketHandler = SocketHandler.getInstance();
          socketHandler.unsubscribeFromNotification(userInfo.id.toString());
          socketSubscribedRef.current = false;
          console.log("Successfully unsubscribed from notification socket");
        }
      } catch (error) {
        console.error("Failed to unsubscribe from socket:", error);
      }
    };
  }, [userInfo?.id, currentProfile?.id]);

  // coffee call
  const setCoffeeCallNotification = useSidebarStore(
    (state) => state.setCoffeeCallNotification
  );
  const coffeeCallNotification = useSidebarStore(
    (state) => state.coffeeCallNotification
  );

  //regular call notification
  const setRegularCallNotification = useSidebarStore(
    (state) => state.setRegularCallNotification
  );
  const regularCallNotification = useSidebarStore(
    (state) => state.regularCallNotification
  );

  // group call invitation
  const setGroupCallInvitation = useSidebarStore(
    (state) => state.setGroupCallInvitation
  );
  const groupCallInvitation = useSidebarStore(
    (state) => state.groupCallInvitation
  );

  // group call notification
  const setGroupCallNotification = useSidebarStore(
    (state) => state.setGroupCallNotification
  );
  const groupCallNotification = useSidebarStore(
    (state) => state.groupCallNotification
  );

  //  call confirmation
  const setCallConfirmation = useSidebarStore(
    (state) => state.setCallConfirmation
  );
  const callConfirmation = useSidebarStore((state) => state.callConfirmation);

  //  call remainder
  const setCallRemainder = useSidebarStore((state) => state.setCallRemainder);
  const callRemainder = useSidebarStore((state) => state.callRemainder);

  const regularCallNotificationFromLocal = JSON.parse(
    localStorage.getItem("regularCallNotification") || "null"
  );
  const groupCallNotificationFromLocal = JSON.parse(
    localStorage.getItem("groupCallNotification") || "null"
  );
  const coffeeCallNotificationfromLocal = JSON.parse(
    localStorage.getItem("coffeeCallNotification") || "null"
  );
  const groupCallInvitationfromLocal = JSON.parse(
    localStorage.getItem("groupCallInvitation") || "null"
  );
  const callConfirmationfromLocal = JSON.parse(
    localStorage.getItem("callConfirmation") || "null"
  );
  const callRemainderfromLocal = JSON.parse(
    localStorage.getItem("callRemainder") || "null"
  );

  useEffect(() => {
    if (!data?.data) return;

    // Coffee call
    if (coffeeCallNotificationfromLocal?.data?.id) {
      const coffeeMatch = data.data.find(
        (item: any) => item.id === coffeeCallNotificationfromLocal.data.id
      );
      if (coffeeMatch && coffeeMatch.status == "Completed") {
        localStorage.removeItem("coffeeCallNotification");
        setCoffeeCallNotification(null); // Clear Zustand
      }
    }

    // Regular call
    if (regularCallNotificationFromLocal?.data?.id) {
      const regularMatch = data.data.find(
        (item: any) =>
          Number(item.id) === Number(regularCallNotificationFromLocal.data.id)
      );
      if (regularMatch && regularMatch.status === "Completed") {
        localStorage.removeItem("regularCallNotification");
        setRegularCallNotification(null); // Clear Zustand
      }
    }

    // group call
    if (groupCallNotificationFromLocal?.data?.id) {
      const groupMatch = data.data.find(
        (item: any) =>
          Number(item.id) === Number(groupCallNotificationFromLocal.data.id)
      );
      if (groupMatch && groupMatch.status === "Completed") {
        localStorage.removeItem("groupCallNotification");
        setRegularCallNotification(null); // Clear Zustand
      }
    }
  }, [
    data,
    coffeeCallNotificationfromLocal,
    groupCallNotificationFromLocal,
    regularCallNotificationFromLocal,
  ]); // Re-run only when bookings data changes

  useEffect(() => {
    if (socketNotification.length > 0) {
      socketNotification.forEach((notification: SocketNotificationData) => {
        if (notification.notification_type === "coffee-call-started") {
          setCoffeeJoinChannel(notification?.channel_name || null);
        } else if (notification.notification_type === "coffee-call-ended") {
          setCoffeeJoinChannel(null);
        } else {
          setCoffeeJoinChannel(null);
        }
      });
    }
  }, [socketNotification]);

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("payload", payload);
      if (payload.data?.type === "coffee_call") {
        enqueueSnackbar("Incoming coffee call!", { variant: "info" });
        setCoffeeCallNotification(payload); // ✅ Zustand not local state
        localStorage.setItem("coffeeCallNotification", JSON.stringify(payload));
      }

      if (
        payload.data?.type === "regular_session" &&
        !payload?.notification?.title?.includes("Healthy Reminder")
      ) {
        enqueueSnackbar("Incoming call!", { variant: "info" });
        setRegularCallNotification(payload);
        localStorage.setItem(
          "regularCallNotification",
          JSON.stringify(payload)
        );
      }

      if (
        (payload.data?.type === "group_session" ||
          payload.data?.type === "regular_session") &&
        payload?.notification?.title?.includes("Healthy Reminder")
      ) {
        enqueueSnackbar(payload?.notification?.body, { variant: "info" });
        setCallRemainder(payload);
        localStorage.setItem("callRemainder", JSON.stringify(payload));
      }

      if (
        payload.data?.type === "group_session" &&
        !payload?.notification?.title?.includes("Healthy Reminder")
      ) {
        enqueueSnackbar("Incoming group call!", { variant: "info" });
        setGroupCallNotification(payload);
        localStorage.setItem("groupCallNotification", JSON.stringify(payload));
      }

      if (payload.data?.type === "booking_accepted") {
        enqueueSnackbar("Your booking has been accepted!", { variant: "info" });
        setCallConfirmation(payload);
        localStorage.setItem("callConfirmation", JSON.stringify(payload));
      }

      if (payload.data?.type === "booking_invite") {
        enqueueSnackbar("You have an invitation!", { variant: "info" });
        setGroupCallInvitation(payload);
        localStorage.setItem("groupCallInvitation", JSON.stringify(payload));
      }
    });

    return () => unsubscribe();
  }, []);

  const CreateCard = () => navigate("/create-card");

  const pathSegments = location.pathname.split("/").filter(Boolean);

  const handleBreadcrumbClick = (index: number) => {
    const path = "/" + pathSegments.slice(0, index + 1).join("/");
    navigate(path);
  };

  const handleClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Header
        setBadgeVisible={setBadgeVisible}
        badgeVisible={badgeVisible}
        socketNotification={socketNotification}
        toggleDrawer={toggleDrawer}
        onClickCreate={CreateCard}
        coffeeCallNotificationfromLayout={
          coffeeCallNotification || coffeeCallNotificationfromLocal
        }
        regularCallNotificationfromLayout={
          regularCallNotification || regularCallNotificationFromLocal
        }
        groupCallNotificationfromLayout={
          groupCallNotification || groupCallNotificationFromLocal
        }
        groupCallInvitationfromLayout={
          groupCallInvitation || groupCallInvitationfromLocal
        }
        callConfirmationfromLayout={
          callConfirmation || callConfirmationfromLocal
        }
        callRemainderfromLayout={callRemainder || callRemainderfromLocal}
      />
      <UserSidebar
        handleClose={handleClose}
        open={drawerOpen}
        toggleDrawer={toggleDrawer}
        coffeeJoinChannel={coffeeJoinChannel}
        // coffeeCallNotification={
        //   coffeeCallNotification || coffeeCallNotificationfromLocal
        // }
        // coffeeCallChannel={
        //   coffeeCallNotification?.data?.channel_name ||
        //   COFFEE_CALL_DUMMY_CHANNEL
        // }
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          px: { xs: 1, md: 3 },
          py: 3,
          overflow: "hidden",
        }}
      >
        <Toolbar />
        {/* <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer" }}
          ></Link>
          {pathSegments.map((seg, index) => {
            const isLast = index === pathSegments.length - 1;
            const label =
              seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
            return isLast ? (
              <Typography color="text.primary" key={index}>
                {label}
              </Typography>
            ) : (
              <Link
                key={index}
                underline="hover"
                color="inherit"
                sx={{ cursor: "pointer" }}
                onClick={() => handleBreadcrumbClick(index)}
              >
                {label}
              </Link>
            );
          })}
        </Breadcrumbs> */}
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default UserLayout;
